import React, { useState, useEffect, useMemo } from 'react';
import useGetProductGroups from 'src/store/hooks/useGetProductGroups';
import useGetCategory from 'src/store/hooks/useGetCategory';
import useCreateCategory from 'src/store/hooks/useCreateCategory';
import useCreateSubcategory from 'src/store/hooks/useCreateSubcategory';
import { useQueryClient } from '@tanstack/react-query';

import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Column from '@amzn/meridian/column';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Heading from '@amzn/meridian/heading';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import Modal, { ModalFooter } from '@amzn/meridian/modal';

import { SwitchButtonValue } from '../agile/types/zoneMapType';
import { ProductItem } from '../../types/productTypes';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import useCategoryGroups from '../../hooks/useCategoryGroups';
import useFilteredOptions from '../../hooks/useFilteredOptions';
import useProductItems from '../../hooks/useProductItems';
import useCategoryId from '../../hooks/useCategoryId';
import ErrorModal from '../shared/modals/ErrorModal';

type ProductViewType = SwitchButtonValue | 'Product Table';

export interface ProductCreationMenuProps {
  switchButtonValue: ProductViewType;
  onDataUpdate?: (data: {
    productItems: ProductItem[];
    productGroupId: string;
    categoryData: string;
    extraDimension: string;
    subcategoryData: string;
    isLoading: boolean;
  }) => void;
  onToastChange?: (type: 'success' | 'error' | 'informational', message: string, timeout?: number) => void;
}

const ProductCreationMenu = (props: ProductCreationMenuProps) => {
  const config = useProgramConfig();
  // Build local labels for use inside this component (dropdown labels, toast messages, modal copy).
  // ProductTable reads these from useProgramConfig() directly and no longer needs them passed through.
  const labels = useMemo(
    () => ({
      productGroupLabel: config.labels.productGroup,
      categoryLabel: config.labels.category,
      extraDimensionLabel: config.features.extraDimension?.label,
      subcategoryLabel: config.labels.subcategory,
      hasExtraDimension: !!config.features.extraDimension,
    }),
    [config],
  );

  const { switchButtonValue, onToastChange } = props;

  // Add state variables for dropdown filters and selections
  const [productGroupFilter, setProductGroupFilter] = useState('');
  const [selectedProductGroup, setSelectedProductGroup] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [extraDimensionFilter, setExtraDimensionFilter] = useState('');
  const [selectedExtraDimension, setSelectedExtraDimension] = useState('');
  const [productTypeFilter, setProductTypeFilter] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');

  // State for modal pop-ups for creation
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [creationType, setCreationType] = useState<
    'category' | 'dimension' | 'subcategory' | 'category-with-dimension' | null
  >(null);
  const [creationName, setCreationName] = useState('');
  const [creationCategoryName, setCreationCategoryName] = useState('');
  const [creationDimensionName, setCreationDimensionName] = useState('');

  // State for error modal
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorModalContent, setErrorModalContent] = useState({
    title: '',
    message: '',
  });

  const {
    productGroupList,
    isLoading,
    hasError: hasProductGroupsError,
    errorMessage: productGroupsErrorMessage,
  } = useGetProductGroups();

  const {
    hasError: hasCategoryError,
    errorMessage: categoryErrorMessage,
    isLoading: isCategoryLoading,
  } = useGetCategory({
    productGroup: selectedProductGroup,
  });

  const { mutate: createCategory, isLoading: isCreatingCategory } = useCreateCategory();
  const { mutate: createSubcategory, isLoading: isCreatingSubcategory } = useCreateSubcategory();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (hasProductGroupsError && productGroupsErrorMessage) {
      setErrorModalContent({
        title: 'Error Loading Data',
        message: `Failed to load ${labels.productGroupLabel}. Please try again later.`,
      });
      setIsErrorModalOpen(true);
    }
  }, [labels.productGroupLabel, labels.categoryLabel, hasProductGroupsError, productGroupsErrorMessage]);

  // Check for errors in category data fetching
  useEffect(() => {
    if (hasCategoryError && selectedProductGroup && categoryErrorMessage) {
      setErrorModalContent({
        title: 'Error Loading Data',
        message: `Failed to load ${labels.categoryLabel} data for the selected ${labels.productGroupLabel}. 
                Please try again later.`,
      });
      setIsErrorModalOpen(true);
    }
  }, [labels.categoryLabel, labels.productGroupLabel, hasCategoryError, categoryErrorMessage, selectedProductGroup]);

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const filteredProductGroupOptions = useFilteredOptions(
    productGroupList,
    productGroupFilter,
    (option) => ({ label: option.name, value: option.id }),
    (option) => option.name,
  );

  // Get category & subcategory data based on selected product group
  const productTaxonomy = useGetCategory({
    productGroup: selectedProductGroup,
  });
  const { subcategoryList: fetchedSubcategoryList } = productTaxonomy;

  // Use server categories directly
  const categoryGroups = useCategoryGroups(productTaxonomy?.categoryList || []);
  let uniqueCategoryOptions = useFilteredOptions(Object.values(categoryGroups), categoryFilter, (group) => ({
    label: group.categoryName,
    value: group.categoryName,
  }));

  // For programs WITH extradimensions, add auto-selection behavior
  if (
    labels.hasExtraDimension &&
    categoryFilter &&
    !uniqueCategoryOptions.some((option) => option.value === categoryFilter)
  ) {
    uniqueCategoryOptions = [{ label: `${categoryFilter} (new)`, value: categoryFilter }, ...uniqueCategoryOptions];

    // Auto-select the new category if it's not already selected
    if (selectedCategoryName !== categoryFilter) {
      setSelectedCategoryName(categoryFilter);
      setSelectedExtraDimension('');
      setSelectedSubcategory('');
    }
  }

  // For programs WITH extradimensions, ensure selected category appears in dropdown options
  if (
    labels.hasExtraDimension &&
    selectedCategoryName &&
    !uniqueCategoryOptions.some((option) => option.value === selectedCategoryName)
  ) {
    uniqueCategoryOptions = [{ label: selectedCategoryName, value: selectedCategoryName }, ...uniqueCategoryOptions];
  }

  // Get the selected category group
  const selectedCategoryGroup = selectedCategoryName ? categoryGroups[selectedCategoryName] : undefined;
  const hasExtraDimensions =
    selectedCategoryGroup && selectedCategoryGroup.extraDimensions && selectedCategoryGroup.extraDimensions.length > 0;

  // Create a formatted list of extra dimensions for dropdown menu if they exist
  const extraDimensionsList =
    hasExtraDimensions && selectedCategoryGroup?.extraDimensions
      ? selectedCategoryGroup.extraDimensions.map((dimension: string) => ({
          label: dimension,
          value: dimension,
        }))
      : [];

  // Filter extra dimensions based on search input
  const filteredExtraDimensions = useFilteredOptions(extraDimensionsList, extraDimensionFilter, (option) => option);

  const selectedCategoryId = useCategoryId(selectedCategoryName, selectedExtraDimension, categoryGroups);

  // Retrieve the subcategories for the selected categoryId
  const filteredSubcategories =
    fetchedSubcategoryList?.filter((subcategory) => {
      // filter by matching categoryId to selected subcategory items
      if (selectedCategoryId) {
        return subcategory.categoryId === selectedCategoryId;
      }
      // If no extraDimension selected, filter from base category
      if (selectedCategoryName && !selectedExtraDimension && selectedCategoryGroup) {
        const baseCategoryId = selectedCategoryGroup.categoryIds[''];
        return subcategory.categoryId === baseCategoryId;
      }
      return false;
    }) || [];

  const subcategoryList =
    filteredSubcategories.map((subcategory: { subcategoryName: string; subcategoryId: string }) => ({
      label: subcategory.subcategoryName,
      value: subcategory.subcategoryId,
    })) || [];

  const filteredProductTypes = useFilteredOptions(subcategoryList, productTypeFilter, (option) => option);

  const productItems = useProductItems(
    selectedProductGroup,
    productGroupList,
    productTaxonomy?.categoryList,
    productTaxonomy?.subcategoryList,
  );

  const handleProductGroupChange = (productGroup: string) => {
    setSelectedProductGroup(productGroup);
    setSelectedCategoryName('');
    setSelectedExtraDimension('');
    setSelectedSubcategory('');
  };

  const handleCategoryChange = (categoryName: string) => {
    if (categoryName === 'create-new-category') {
      // For programs without extradimensions, create category immediately
      if (!labels.hasExtraDimension) {
        setCreationName(categoryFilter);
        setCreationType('category');
        setIsModalOpen(true);
      } else {
        // For programs with extradimensions, just set the category name and wait for extradimension
        setSelectedCategoryName(categoryFilter);
        setSelectedExtraDimension('');
        setSelectedSubcategory('');
      }
    } else {
      setSelectedCategoryName(categoryName);
      // Reset extraDimension and subcategory dropdowns when category changes
      setSelectedExtraDimension('');
      setSelectedSubcategory('');
    }
  };

  const handleExtraDimensionChange = (dimension: string) => {
    if (dimension === 'create-new-dimension') {
      // Determine category name source: new (from filter) or existing (from selection)
      const isCreatingNewCategory = uniqueCategoryOptions.length === 0 && categoryFilter;
      const categoryName = isCreatingNewCategory ? categoryFilter : selectedCategoryName;

      // Store category and dimension names separately
      setCreationCategoryName(categoryName);
      setCreationDimensionName(extraDimensionFilter);
      setCreationType('category-with-dimension');
      setIsModalOpen(true);
    } else {
      setSelectedExtraDimension(dimension);
      // Reset subcategory dropdown when extraDimension changes
      setSelectedSubcategory('');
    }
  };

  const handleSubcategoryChange = (productType: string) => {
    if (productType === 'create-new-subcategory') {
      // Open modal for confirmation
      setCreationName(productTypeFilter);
      setCreationType('subcategory');
      setIsModalOpen(true);
    } else {
      setSelectedSubcategory(productType);
    }
  };

  // Send data to ProductGroupPage to pass to productTable
  const { onDataUpdate } = props;
  useEffect(() => {
    if (onDataUpdate) {
      onDataUpdate({
        productItems,
        productGroupId: selectedProductGroup,
        categoryData: selectedCategoryName,
        extraDimension: selectedExtraDimension,
        subcategoryData: selectedSubcategory,
        isLoading: isCategoryLoading,
      });
    }
  }, [
    productItems,
    selectedProductGroup,
    selectedCategoryName,
    selectedExtraDimension,
    selectedSubcategory,
    onDataUpdate,
    isCategoryLoading,
  ]);

  const onCancel = () => {
    // Show informational alert toast when creation is cancelled
    if (onToastChange) {
      const typeLabel =
        creationType === 'category' ? 'category' : creationType === 'dimension' ? 'dimension' : 'subcategory';
      onToastChange('informational', `Creation of ${typeLabel} "${creationName}" was cancelled`, 5000);
    }
    setIsModalOpen(false);
  };

  const onConfirm = () => {
    // Handle category creation (unified approach for all category types)
    if (creationType === 'category' || creationType === 'category-with-dimension') {
      let categoryName = '';
      let dimensionName = '';

      // Get names based on creation type
      if (creationType === 'category-with-dimension') {
        categoryName = creationCategoryName;
        dimensionName = creationDimensionName;
      } else {
        // creationType === 'category'
        categoryName = creationName;
        // dimensionName remains empty for simple categories
      }

      // For programs with extradimensions, create with the named dimension
      // For programs without extradimensions, pass null
      const extraDimensionsToCreate = labels.hasExtraDimension ? [dimensionName] : null;

      createCategory(
        {
          input: {
            productGroup: selectedProductGroup,
            categoryName: categoryName,
            extraDimensions: extraDimensionsToCreate,
          },
        },
        {
          onSuccess: (response) => {
            // Check if the response contains an error
            const hasError = response?.createCategory?.error !== null && response?.createCategory?.error !== undefined;

            if (hasError) {
              // Show error modal toast to user for failed category creation
              if (onToastChange) {
                onToastChange(
                  'error',
                  `Failed to create new ${labels.categoryLabel}: "${categoryName}" for ${selectedProductGroup}`,
                  5000,
                );
              }
            } else {
              // Invalidate the category query to refresh the category list
              queryClient.invalidateQueries({
                queryKey: ['GetCategory', { input: { productGroup: selectedProductGroup } }],
              });

              // Set selections after successful creation
              setSelectedCategoryName(categoryName);
              if (labels.hasExtraDimension) {
                setSelectedExtraDimension(dimensionName);
              } else {
                setSelectedExtraDimension('');
              }
              setSelectedSubcategory('');

              // Show success alert
              if (onToastChange) {
                const successMessage = labels.hasExtraDimension
                  ? `Successfully created new ${labels.categoryLabel}: "${categoryName}" with ${labels.extraDimensionLabel}: "${dimensionName}" for ${selectedProductGroup}`
                  : `Successfully created new ${labels.categoryLabel}: "${categoryName}" for ${selectedProductGroup}`;
                onToastChange('success', successMessage, 5000);
              }
            }
            setIsModalOpen(false);
          },
          onError: () => {
            // handle request level network error if creation fails
            if (onToastChange) {
              onToastChange(
                'error',
                `Failed to create new ${labels.categoryLabel}: "${categoryName}" for ${selectedProductGroup}`,
                5000,
              );
            }
            setIsModalOpen(false);
          },
        },
      );
    } else if (creationType === 'subcategory') {
      if (!selectedCategoryId) {
        // This shouldn't happen if our UI logic is correct, but handle it gracefully
        if (onToastChange) {
          onToastChange('error', `Cannot create subcategory: No category ID found for "${selectedCategoryName}"`, 5000);
        }
        setIsModalOpen(false);
        return;
      }

      // Create new subcategory
      createSubcategory(
        {
          input: {
            subcategoryName: creationName,
            categoryId: selectedCategoryId,
          },
        },
        {
          onSuccess: (response) => {
            // Check if the response contains an error
            const hasError =
              response?.createSubcategory?.error !== null && response?.createSubcategory?.error !== undefined;

            if (hasError) {
              // handle error response from graphql server if creation results in failure
              if (onToastChange) {
                onToastChange(
                  'error',
                  `Failed to create new ${labels.subcategoryLabel}: "${creationName}" for ${selectedCategoryName}`,
                  5000,
                );
              }
            } else {
              // Set the selected subcategory to the newly created one
              setSelectedSubcategory(response?.createSubcategory?.subcategory?.subcategoryId || creationName);

              // Invalidate the category query to refresh the subcategory list for the table
              queryClient.invalidateQueries({
                queryKey: ['GetCategory', { input: { productGroup: selectedProductGroup } }],
              });

              // Show success alert if successfully created
              if (onToastChange) {
                onToastChange(
                  'success',
                  `Successfully created new ${labels.subcategoryLabel}: 
                    "${creationName}" for "${selectedCategoryName}"`,
                  5000,
                );
              }
            }
            setIsModalOpen(false);
          },
          onError: () => {
            // handle request level network error if creation fails
            if (onToastChange) {
              onToastChange(
                'error',
                `Failed to create new ${labels.subcategoryLabel}: "${creationName}" for ${selectedCategoryName}`,
                5000,
              );
            }
            setIsModalOpen(false);
          },
        },
      );
    }
  };

  const renderConfirmationModal = () => {
    const title =
      creationType === 'category' || creationType === 'category-with-dimension'
        ? `Confirm ${labels.categoryLabel} Creation`
        : creationType === 'dimension'
          ? `Confirm ${labels.extraDimensionLabel} Creation`
          : `Confirm ${labels.subcategoryLabel} Creation`;

    let description = '';
    if (creationType === 'category') {
      description = labels.hasExtraDimension
        ? `You are about to create a new ${labels.categoryLabel}: "${creationName}" with "${creationName}" ${labels.extraDimensionLabel}. This will be added to the database. Are you sure?`
        : `You are about to create a new ${labels.categoryLabel}: "${creationName}". This will be added to the database. Are you sure?`;
    } else if (creationType === 'category-with-dimension') {
      description = `You are about to create a new ${labels.categoryLabel}: "${creationCategoryName}" with "${creationDimensionName}" ${labels.extraDimensionLabel}. This will be added to the database. Are you sure?`;
    } else if (creationType === 'dimension') {
      description = `You are about to create a new ${labels.extraDimensionLabel}: "${creationName}" for ${labels.categoryLabel} "${selectedCategoryName}". This will be added to the database. Are you sure?`;
    } else {
      description = `You are about to create a new ${labels.subcategoryLabel}: "${creationName}" for ${labels.categoryLabel} "${selectedCategoryName}". This will be added to the database. Are you sure?`;
    }

    const isCreating = isCreatingCategory || isCreatingSubcategory;

    return (
      <Modal title={title} open={isModalOpen} scrollContainer="viewport" aria-describedby="modal-description">
        <Text id="modal-description">{description}</Text>
        <ModalFooter>
          <Row alignmentHorizontal="end" widths="fit">
            <Button type="secondary" size="small" onClick={onCancel} disabled={isCreating}>
              Cancel
            </Button>
            <Button type="primary" size="small" onClick={onConfirm} disabled={isCreating}>
              {isCreating ? (
                <Row spacing="200" alignmentVertical="center">
                  <Loader size="small" />
                  <Text>Creating...</Text>
                </Row>
              ) : (
                'Confirm'
              )}
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
    );
  };

  return (
    <Column spacingInset="400" spacing="400" height="100%" overflowY="auto">
      {isModalOpen && renderConfirmationModal()}
      <ErrorModal
        isOpen={isErrorModalOpen}
        title={errorModalContent.title}
        message={errorModalContent.message}
        onClose={closeErrorModal}
      />
      <Heading level={3}>Product Groups</Heading>

      {/* Product Group Dropdown */}
      <Row spacing="200" alignmentVertical="bottom">
        <Select
          label={labels.productGroupLabel}
          value={selectedProductGroup}
          onChange={handleProductGroupChange}
          searchQuery={productGroupFilter}
          onSearch={setProductGroupFilter}
          placeholder={`Choose a ${labels.productGroupLabel}...`}
          width={300}
        >
          {filteredProductGroupOptions.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
          {isLoading && (
            <Column alignmentVertical="center" spacing="300" spacingInset="600">
              <Loader size="small" />
            </Column>
          )}
          {!isLoading && filteredProductGroupOptions.length === 0 && (
            <Column alignmentVertical="center" spacing="300" spacingInset="600">
              <Text alignment="center">No results</Text>
            </Column>
          )}
        </Select>
      </Row>
      {switchButtonValue !== undefined && !selectedProductGroup && (
        <Alert size="small" type="error">
          Please select a {labels.productGroupLabel}
        </Alert>
      )}

      {/* Category dropdown  */}
      <Row spacing="200" alignmentVertical="bottom">
        <Select
          label={labels.categoryLabel}
          value={selectedCategoryName}
          onChange={handleCategoryChange}
          searchQuery={categoryFilter}
          onSearch={setCategoryFilter}
          placeholder={`Choose a ${labels.categoryLabel}...`}
          width={300}
        >
          {uniqueCategoryOptions.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
          {/* Show "Create new" option when there's a search query with no matches */}
          {categoryFilter && !uniqueCategoryOptions.some((option) => option.value === categoryFilter) && (
            <SelectOption
              label={`Create new: "${categoryFilter}"`}
              value="create-new-category"
              key="create-new-category"
            />
          )}
          {/* Show "No results" only when there's no matches and no search query */}
          {uniqueCategoryOptions.length === 0 && !categoryFilter && (
            <Column alignmentVertical="center" spacing="300" spacingInset="600">
              <Text alignment="center">No results</Text>
            </Column>
          )}
        </Select>

        {selectedCategoryName && (
          <Tooltip position="end" title="Clear Selection">
            <Button
              type="tertiary"
              size="small"
              aria-label="Clear" // accessibility
              onClick={() => handleCategoryChange('')}
            >
              <Icon tokens={closeSmallTokens} />
            </Button>
          </Tooltip>
        )}
      </Row>

      {/* Extra Dimensions dropdown */}
      {labels.hasExtraDimension && (
        <>
          <Row spacing="200" alignmentVertical="bottom">
            <Select
              label={labels.extraDimensionLabel}
              value={selectedExtraDimension}
              onChange={handleExtraDimensionChange}
              searchQuery={extraDimensionFilter}
              onSearch={setExtraDimensionFilter}
              placeholder={`Choose a ${labels.extraDimensionLabel}...`}
              width={300}
            >
              {filteredExtraDimensions.map((option) => (
                <SelectOption label={option.label} value={option.value} key={option.value} />
              ))}
              {/* Show "Create new" option when there's a search query with no matches */}
              {/* Sub dropzone */}
              {extraDimensionFilter &&
                !filteredExtraDimensions.some(
                  (option) => option.label.toLowerCase() === extraDimensionFilter.toLowerCase(),
                ) && (
                  <SelectOption
                    label={`Create new: "${extraDimensionFilter}"`}
                    value="create-new-dimension"
                    key="create-new-dimension"
                  />
                )}
              {/* Show "No results" only when there's no matches and no search query */}
              {filteredExtraDimensions.length === 0 && !extraDimensionFilter && (
                <Column alignmentVertical="center" spacing="300" spacingInset="600">
                  <Text alignment="center">No results</Text>
                </Column>
              )}
            </Select>

            {selectedExtraDimension && (
              <Tooltip position="end" title="Clear Selection">
                <Button
                  type="tertiary"
                  size="small"
                  aria-label="Clear" // accessibility
                  onClick={() => handleExtraDimensionChange('')}
                >
                  <Icon tokens={closeSmallTokens} />
                </Button>
              </Tooltip>
            )}
          </Row>
        </>
      )}

      {/* Subcategory dropdown or Velocity */}
      <Row spacing="200" alignmentVertical="bottom">
        <Select
          label={labels.subcategoryLabel}
          value={selectedSubcategory}
          onChange={handleSubcategoryChange}
          searchQuery={productTypeFilter}
          onSearch={setProductTypeFilter}
          placeholder={`Choose a ${labels.subcategoryLabel}...`}
          width={300}
        >
          {filteredProductTypes.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
          {/* Show "Create new" option when there's a search query and we have a categoryId */}
          {productTypeFilter &&
            selectedCategoryId &&
            !filteredProductTypes.some((option) => option.label.toLowerCase() === productTypeFilter.toLowerCase()) && (
              <SelectOption
                label={`Create new: "${productTypeFilter}"`}
                value="create-new-subcategory"
                key="create-new-subcategory"
              />
            )}
          {/* Show "No results" only when there's no matches and no search query */}
          {filteredProductTypes.length === 0 && !productTypeFilter && (
            <Column alignmentVertical="center" spacing="300" spacingInset="600">
              <Text alignment="center">No results</Text>
            </Column>
          )}
        </Select>
      </Row>
    </Column>
  );
};

export default ProductCreationMenu;
